<template>
    <div style="min-height: calc(100vh - 158px);" class="card">
        <PageHeader
            :showBtn="false"
            :showAddNew="false"
            title="Synced E-commerce Products"
        >
            <template #extra-btn>
                <FilterPopover
                    ref="filterPopoverRef"
                    popover-width="320px"
                    :active-count="activeFilterCount"
                    @open="onOpenFilter"
                    @apply="onApplyFilters"
                    @clear="onClearFilters"
                >
                    <div class="col-12">
                        <label class="filter-label">Status</label>
                        <v-select
                            v-model="draftStatus"
                            :options="statusOptions"
                            label="name"
                            :reduce="option => option.id"
                            placeholder="All status"
                        />
                    </div>
                </FilterPopover>
            </template>
        </PageHeader>

        <div class="p-2 pb-5">
            <div class="row g-1 mb-2">
                <div class="col-12 col-md-4">
                    <div class="summary-tile">
                        <span class="summary-label">Awaiting review</span>
                        <span class="summary-value text-warning">{{ totalPending }}</span>
                    </div>
                </div>
                <div class="col-12 col-md-4">
                    <div class="summary-tile">
                        <span class="summary-label">Approved</span>
                        <span class="summary-value text-success">{{ totalApproved }}</span>
                    </div>
                </div>
                <div class="col-12 col-md-4">
                    <div class="summary-tile">
                        <span class="summary-label">Rejected</span>
                        <span class="summary-value text-danger">{{ totalRejected }}</span>
                    </div>
                </div>
            </div>

            <div class="table-responsive">
                <table class="table table-hover table-bordered">
                    <thead>
                        <tr>
                            <th>Sync Date</th>
                            <th>Reference</th>
                            <th class="text-center">Total</th>
                            <th class="text-center">Awaiting</th>
                            <th class="text-center">Approved</th>
                            <th class="text-center">Rejected</th>
                            <th class="text-center">Status</th>
                            <th class="text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="item in logs" :key="item.id">
                            <td>{{ syncedAt(item.created_at) }}</td>
                            <td class="fw-bold">{{ item.batch }}</td>
                            <td class="text-center">{{ item.total_items }}</td>
                            <td class="text-center">
                                <span v-if="item.pending_items > 0" class="badge bg-warning">{{ item.pending_items }}</span>
                                <span v-if="item.pending_items < 1" class="text-muted">0</span>
                            </td>
                            <td class="text-center">
                                <span v-if="item.approved_items > 0">{{ item.approved_items }}</span>
                                <span v-if="item.approved_items < 1" class="text-muted">0</span>
                            </td>
                            <td class="text-center">
                                <span v-if="item.rejected_items > 0" class="badge bg-light-danger text-danger">{{ item.rejected_items }}</span>
                                <span v-if="item.rejected_items < 1" class="text-muted">0</span>
                            </td>
                            <td class="text-center text-capitalize">
                                <span :class="statusClass(item.status)">{{ item.status }}</span>
                            </td>
                            <td class="text-center">
                                <button
                                    v-if="item.pending_items > 0"
                                    @click="goToReview(item.id)"
                                    class="btn btn-sm btn-primary"
                                >Review</button>
                                <button
                                    v-if="item.pending_items < 1"
                                    @click="onDelete(item.id)"
                                    class="btn btn-sm btn-outline-danger"
                                >Delete</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <p v-if="! logs.length && ! loading" class="text-center mt-4 text-muted">
                    No product has been synced from the storefront yet.
                </p>
            </div>
        </div>

        <div class="px-2 position-absolute bottom-0 mt-4">
            <Pagination :offset="params.offset" @onPageChange="onPageChange" ref="pagination"/>
        </div>

        <Loader v-if="loading"/>
    </div>
</template>

<script setup>
import { ref, computed, inject, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import handleEcommerce from '@/services/modules/ecommerce'
import useDate from '@/services/utils/day'
import PageHeader from '@/components/atom/PageHeader'
import FilterPopover from '@/components/atom/FilterPopover'
import Loader from '@/components/atom/LoaderComponent'
import Pagination from '@/components/atom/Pagination'

const store = useStore()
const route = useRoute()
const router = useRouter()
const showError = inject('showError')
const showSuccess = inject('showSuccess')

const { fetchSyncProductList, deleteSyncProductLog } = handleEcommerce()
const { formatDateWithTime } = useDate()

const logs = ref([])
const loading = ref(false)
const status = ref(null)
const draftStatus = ref(null)
const filterPopoverRef = ref(null)
const params = ref({
    offset: 20,
})
const summary = ref({
    pending_items: 0,
    approved_items: 0,
    rejected_items: 0,
})
const statusOptions = ref([
    { id: 'pending', name: 'Pending' },
    { id: 'processing', name: 'Processing' },
    { id: 'completed', name: 'Completed' }
])

const companyId = computed(() => route.params.companyId)
const totalPending = computed(() => summary.value.pending_items)
const totalApproved = computed(() => summary.value.approved_items)
const totalRejected = computed(() => summary.value.rejected_items)
const activeFilterCount = computed(() => {
    if (! status.value) return 0
    return 1
})

const syncedAt = (value) => {
    if (! value) return ''
    return formatDateWithTime(value, false)
}

const statusClass = (value) => {
    if (value === 'completed') return 'badge bg-success'
    if (value === 'processing') return 'badge bg-info'
    return 'badge bg-warning'
}

const getQuery = () => {
    let query = `?company_id=${companyId.value}`
    if (status.value) query += `&status=${status.value}`
    if (route.query.page) query += `&page=${route.query.page}`
    query += `&per_page=${params.value.offset}`
    return query
}

const setPagination = (data) => {
    store.commit('setPaginationData', {
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    })
}

const getLogList = async () => {
    try {
        loading.value = true
        const res = await fetchSyncProductList(getQuery())
        if (! res.status) {
            logs.value = []
            store.commit('resetPagination')
            return
        }
        logs.value = res.data.data
        summary.value = res.summary
        setPagination(res.data)
    } catch (err) {
        showError('Something went wrong')
    } finally {
        loading.value = false
    }
}

const onPageChange = async (page) => {
    let routeQuery = Object.assign({}, route.query)
    routeQuery.page = page
    await router.push({ path: route.path, query: routeQuery })
    await getLogList()
}

const reloadFromFirstPage = async () => {
    let routeQuery = Object.assign({}, route.query)
    delete routeQuery.page
    await router.push({ path: route.path, query: routeQuery })
    await getLogList()
}

const onOpenFilter = () => {
    draftStatus.value = status.value
}

const onApplyFilters = async () => {
    status.value = draftStatus.value
    await reloadFromFirstPage()
}

const onClearFilters = async () => {
    status.value = null
    draftStatus.value = null
    await reloadFromFirstPage()
}

const refreshAfterDelete = async () => {
    const isLastRowOfPage = logs.value.length < 2
    const currentPage = Number(route.query.page)
    if (! isLastRowOfPage || ! currentPage || currentPage < 2) return getLogList()
    return onPageChange(currentPage - 1)
}

const onDelete = async (id) => {
    if (! confirm('Delete this synced product list?')) return

    try {
        loading.value = true
        const res = await deleteSyncProductLog(id, `?company_id=${companyId.value}`)
        if (! res.status) {
            showError(res.message)
            return
        }
        showSuccess(res.message)
        await refreshAfterDelete()
    } catch (err) {
        showError('Something went wrong')
    } finally {
        loading.value = false
    }
}

const goToReview = (id) => {
    router.push({
        name: 'sync-ecommerce-product-review',
        params: Object.assign({}, route.params, { uploadLogId: id }),
        query: route.query
    })
}

onMounted(() => {
    getLogList()
})
</script>

<style scoped>
.summary-tile {
    background: #F8FAFC;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.summary-label {
    font-size: 12px;
    color: #101828;
}
.text-muted {
    color: #101828 !important;
}
.summary-value {
    font-size: 22px;
    font-weight: 600;
}
</style>
